import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  QueryList,
  ViewChildren,
  OnDestroy,
  OnInit
} from '@angular/core';
import {
  NavigationEnd,
  Router,
  RouterLink,
  RouterLinkActive
} from '@angular/router';
import {AsyncPipe, NgClass, NgIf, NgOptimizedImage, NgStyle} from '@angular/common';
import {combineLatest, Subscription} from 'rxjs';

import {LayoutService} from '../../../../core/services/layout.service';
import {SafeHtmlPipe} from '../../../../shared/pipes/safe-html.pipe';

export interface NavItem {
  name: string;
  icon: string;
  path?: string;
  new?: boolean;
  subItems?: {
    name: string;
    path: string;
    pro?: boolean;
    new?: boolean;
  }[];
}

@Component({
  selector: 'app-sidebar',
  standalone: true,
  imports: [RouterLink, RouterLinkActive, AsyncPipe, SafeHtmlPipe, NgClass, NgStyle],
  templateUrl: './app-sidebar.component.html',
  styleUrl: './app-sidebar.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class AppSidebarComponent implements OnInit, OnDestroy {

  /* =====================
     MENU DATA
  ====================== */
  navItems: NavItem[] = [
    /*{
      icon: `📊`,
      name: 'Dashboard',
      path: '/dashboard',
    },*/
    {
      icon: `📅`,
      name: 'Events',
      path: '/events',
    },
   /* {
      icon: `🧑‍💼`,
      name: 'Users',
      path: '/users',
    },
    {
      icon: `🎟️`,
      name: 'Tickets',
      path: '/tickets',
    },
    {
      icon: `📦`,
      name: 'Orders',
      path: '/orders',
    },
    {
      icon: `💰`,
      name: 'Payments',
      path: '/payments',
    },
    {
      icon: `📈`,
      name: 'Reports',
      path: '/reports',
    },
    {
      icon: `⚙️`,
      name: 'Settings',
      path: '/settings',
    },

    // 🔽 Submenu Example
    {
      icon: `🧩`,
      name: 'Management',
      subItems: [
        { name: 'Organizers', path: '/management/organizers' },
        { name: 'Venues', path: '/management/venues' },
        { name: 'Categories', path: '/management/categories' },
        { name: 'Tags', path: '/management/tags' },
        { name: 'Roles', path: '/management/roles' },
      ],
    },

    // 🔽 More items to force scroll
    {
      icon: `📝`,
      name: 'Content',
      path: '/content',
    },
    {
      icon: `🧾`,
      name: 'Invoices',
      path: '/invoices',
    },
    {
      icon: `🔔`,
      name: 'Notifications',
      path: '/notifications',
    },
    {
      icon: `📂`,
      name: 'Files',
      path: '/files',
    },
    {
      icon: `🧪`,
      name: 'Testing',
      path: '/testing',
    },
    {
      icon: `🛠️`,
      name: 'Tools',
      path: '/tools',
    },
    {
      icon: `❓`,
      name: 'Help',
      path: '/help',
    },*/
  ];

  othersItems: NavItem[] = [ /* SAME DATA – unchanged */];

  /* =====================
     UI STATE
  ====================== */
  openSubmenu: string | null = null;
  subMenuHeights: Record<string, number> = {};

  @ViewChildren('subMenu') subMenuRefs!: QueryList<ElementRef>;

  readonly isExpanded$;
  readonly isMobileOpen$;
  readonly isHovered$;

  private subscription = new Subscription();

  constructor(
    public layoutService: LayoutService,
    private router: Router,
    private cdr: ChangeDetectorRef
  ) {
    this.isExpanded$ = this.layoutService.isExpanded$;
    this.isMobileOpen$ = this.layoutService.isMobileOpen$;
    this.isHovered$ = this.layoutService.isHovered$;
  }

  /* =====================
     LIFECYCLE
  ====================== */
  ngOnInit(): void {
    this.subscription.add(
      this.router.events.subscribe(event => {
        if (event instanceof NavigationEnd) {
          this.setActiveMenuFromRoute(event.urlAfterRedirects);
        }
      })
    );

    this.subscription.add(
      combineLatest([
        this.isExpanded$,
        this.isMobileOpen$,
        this.isHovered$
      ]).subscribe(() => {
        this.cdr.markForCheck();
      })
    );

    this.setActiveMenuFromRoute(this.router.url);
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }

  /* =====================
     UI EVENTS
  ====================== */
  onMouseEnter() {
    if (window.innerWidth >= 1200) {
      this.layoutService.setHovered(true);
    }
  }

  onMouseLeave() {
    if (window.innerWidth >= 1200) {
      this.layoutService.setHovered(false);
    }
  }

  isActive(path: string): boolean {
    return this.router.url === path;
  }

  toggleSubmenu(section: string, index: number): void {
    const key = `${section}-${index}`;

    if (this.openSubmenu === key) {
      this.openSubmenu = null;
      this.subMenuHeights[key] = 0;
      return;
    }

    this.openSubmenu = key;

    queueMicrotask(() => {
      const el = document.getElementById(key);
      if (el) {
        this.subMenuHeights[key] = el.scrollHeight;
        this.cdr.markForCheck();
      }
    });
  }

  onSubmenuClick(): void {
    this.layoutService.setMobileOpen(false);
  }

  /* =====================
     ROUTE HANDLING
  ====================== */
  private setActiveMenuFromRoute(url: string): void {
    const groups = [
      {items: this.navItems, prefix: 'main'},
      {items: this.othersItems, prefix: 'others'}
    ];

    for (const group of groups) {
      group.items.forEach((item, index) => {
        item.subItems?.forEach(sub => {
          if (sub.path === url) {
            const key = `${group.prefix}-${index}`;
            this.openSubmenu = key;

            queueMicrotask(() => {
              const el = document.getElementById(key);
              if (el) {
                this.subMenuHeights[key] = el.scrollHeight;
                this.cdr.markForCheck();
              }
            });
          }
        });
      });
    }
  }

  closeSidebar() {
    this.layoutService.setMobileOpen(false);
  }

}
